import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Menu as DropdownMenu,
  MenuItem,
  Skeleton,
  TablePagination,
  InputAdornment,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  // Visibility as ViewIcon,
  // Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import PageLayout from "../components/layout/PageLayout";
import { extractList } from "../utils/response";
import { useLazySelectOptions } from "../hooks/useLazySelectOptions";
import {
  useLazyGetDBAsQuery,
  useLazyGetDbaStatusQuery,
  useLazyGetUsersListQuery,
  useDeleteDbaMutation,
  useUpdateDbaProcessStatusMutation,
  useAssignDbaToUserMutation,
} from "../store/api";
import { toast } from "react-toastify";
import SweetAlert from "../utils/sweetAlert";
import Can from "../components/permissions/Can";
import { useAuth } from "../hooks/useAuth";
import {
  DbaStatusChangeDialog,
  DbaAssignUserDialog,
  getDbaProcessStatusId,
} from "../components/Merchants";
import { getErrorMessage } from "../utils/errorHandler";

const SalesDbaListing = () => {
  const navigate = useNavigate();
  const [fetchDBAs] = useLazyGetDBAsQuery();
  const [fetchDbaStatus] = useLazyGetDbaStatusQuery();
  const [fetchUsersList] = useLazyGetUsersListQuery();
  const [deleteDba] = useDeleteDbaMutation();
  const [updateDbaProcessStatus] = useUpdateDbaProcessStatusMutation();
  const [assignDbaToUser] = useAssignDbaToUserMutation();
  const { hasPermission } = useAuth();

  // permission
  const canViewMerchants = hasPermission('dba-view');
  // const canEditMerchants = hasPermission('dba-edit');
  const canChangeStatus = hasPermission('dba-status');
  const canAssignDBA = hasPermission('dba-reassign');
  const canDeleteMerchants = hasPermission('dba-delete');


  const { options: statusOptions, load: loadStatusOptions } = useLazySelectOptions(
    fetchDbaStatus,
    { args: { "conditions[model]": "dba" }, enabled: canViewMerchants },
  );
  const { options: usersList, load: loadUsersList } = useLazySelectOptions(
    fetchUsersList,
    {
      // Assign dialog shows the user name only — no relations needed
      args: { paginate: 0 },
      enabled: canAssignDBA,
    },
  );

  const [dbaData, setDbaData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [selectedDba, setSelectedDba] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    if (openStatusDialog) loadStatusOptions();
  }, [openStatusDialog, loadStatusOptions]);

  useEffect(() => {
    if (openAssignDialog) loadUsersList();
  }, [openAssignDialog, loadUsersList]);

  const loadDBAData = async (
    page = pagination.page,
    pageSize = pagination.pageSize,
    search = searchValue,
  ) => {
    if (!canViewMerchants) {
      toast.error("You do not have permission to view merchants.");
      return;
    }
    try {
      setTableLoading(true);
      const params = {
        // Only what the table renders: bank name, assignee, status chip
        "relations[]": ["bankAccount", "lastStatusLog", "users"],
        "conditions[legal_info_id]": "",
        "conditions[type]": "sales",
        paginate: 1,
        page: page,
        per_page: pageSize,
        ...(search && { search: search }),
      };
      const response = await fetchDBAs(params).unwrap();
      setDbaData(extractList(response));
      setPagination((prev) => ({
        ...prev,
        total:
          response?.data?.pagination?.total ?? response?.pagination?.total ?? 0,
      }));
    } catch (error) {
      console.error("Error loading DBA data:", error);
      toast.error(error?.data?.message || getErrorMessage(error) || "Failed to load DBA data. Please try again.");
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    loadDBAData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dialog fields must not refetch
  }, [pagination.page, pagination.pageSize]);

  const isFirstSearchEffect = React.useRef(true);
  useEffect(() => {
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false;
      return;
    }
    const timer = setTimeout(() => {
      if (pagination.page !== 1) {
        setPagination((prev) => ({ ...prev, page: 1 }));
        return;
      }
      loadDBAData(1, pagination.pageSize, searchValue);
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchValue("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newSize) => {
    setPagination({ page: 1, pageSize: newSize, total: pagination.total });
  };

  const handleView = (dba) => {
    navigate(`/view-merchant?id=${dba.id}`);
  };

  const handleEdit = (dba) => {
    navigate(`/create-merchant?edit=true&id=${dba.id}`);
  };

  const handleDelete = async (dba) => {
    if (!canDeleteMerchants) {
      toast.error("You do not have permission to delete merchants.");
      return;
    }
    try {
      // Show SweetAlert confirmation
      const result = await SweetAlert.deleteConfirm(dba.name || "this DBA");

      // If user cancels, return early
      if (!result.isConfirmed) {
        return;
      }

      setProcessingId(dba.id);
      await deleteDba(dba.id).unwrap();
      await SweetAlert.success({
        text: "DBA record deleted successfully.",
      });
      await loadDBAData();
    } catch (error) {
      console.error("Delete action error:", error);
      await SweetAlert.error({
        text: error?.data?.message || getErrorMessage(error) || "Failed to delete DBA record",
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleOpenStatusDialog = (dba) => {
    setSelectedDba(dba);
    setNewStatus(getDbaProcessStatusId(dba));
    setOpenStatusDialog(true);
  };

  const handleCloseStatusDialog = () => {
    setOpenStatusDialog(false);
    setSelectedDba(null);
    setNewStatus("");
  };

  const handleChangeStatus = async () => {
    if (!canChangeStatus) {
      toast.error("You do not have permission to change merchant status.");
      return;
    }
    if (!selectedDba || !newStatus) {
      toast.warn("Please select a status.");
      return;
    }

    try {
      setProcessingId(selectedDba.id);
      const response = await updateDbaProcessStatus({
        id: selectedDba.id,
        statusId: newStatus,
      }).unwrap();

      if (response.success) {
        setDbaData((prev) =>
          prev.map((dba) =>
            dba.id === selectedDba.id
              ? {
                  ...dba,
                  status: {
                    ...dba.status,
                    id: newStatus,
                    name:
                      statusOptions.find((option) => option.id === newStatus)
                        ?.name || dba.status?.name,
                  },
                }
              : dba,
          ),
        );

        toast.success("Status updated successfully");
        handleCloseStatusDialog();
      } else {
        throw new Error(response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error?.data?.message || getErrorMessage(error) || "Failed to update status. Please try again.");
    } finally {
      setProcessingId(null);
    }
  };

  const handleAssign = async () => {
    if (!canAssignDBA) {
      toast.error("You do not have permission to assign merchants.");
      return;
    }
    if (!selectedDba || !selectedUser) {
      toast.warn("Please select a user.");
      return;
    }

    try {
      setProcessingId(selectedDba.id);
      const response = await assignDbaToUser({
        id: selectedDba.id,
        userId: selectedUser,
      }).unwrap();

      if (response.success) {
        const assignedUser = usersList.find((user) => user.id === selectedUser);

        setDbaData((prev) =>
          prev.map((dba) =>
            dba.id === selectedDba.id
              ? {
                  ...dba,
                  users: {
                    ...dba.users?.name,
                    name: assignedUser?.name || "Assigned",
                  },
                }
              : dba,
          ),
        );

        toast.success("DBA assigned successfully");
        setOpenAssignDialog(false);
        setSelectedUser("");
      } else {
        throw new Error(response.message || "Failed to assign DBA");
      }
    } catch (error) {
      console.error("Error assigning DBA:", error);
      toast.error(error?.data?.message || getErrorMessage(error) || "Failed to assign DBA. Please try again.");
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={{ color: "#154A8A", fontWeight: 600 }}>
            Sales Applications ({dbaData.length})
          </Typography>

          <TextField
            placeholder="Search by Merchant DBA"
            value={searchValue}
            onChange={handleSearchChange}
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchValue && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Paper sx={{ boxShadow: 1 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                    Merchant DBA
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                    Bank Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                    DBA Email
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                    Assigned To
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                    Date Stamp
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "grey.700" }}>
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: "grey.700",
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableLoading ? (
                  // Skeleton loading rows
                  [...Array(pagination.pageSize)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton animation="wave" height={30} />
                      </TableCell>
                      <TableCell>
                        <Skeleton animation="wave" height={30} />
                      </TableCell>
                      <TableCell>
                        <Skeleton animation="wave" height={30} />
                      </TableCell>
                      <TableCell>
                        <Skeleton animation="wave" height={30} />
                      </TableCell>
                      <TableCell>
                        <Skeleton animation="wave" height={30} />
                      </TableCell>
                      <TableCell>
                        <Skeleton animation="wave" height={30} width={80} />
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Skeleton animation="wave" height={30} width={40} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : dbaData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchValue
                          ? "No records found for your search"
                          : "There's no records to display"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  dbaData.map((dba) => (
                    <TableRow key={dba.id} hover>
                      <TableCell>{dba.name || "N/A"}</TableCell>
                      <TableCell>
                        {dba?.bank_account?.bank_name || "N/A"}
                      </TableCell>
                      <TableCell>{dba?.email || "N/A"}</TableCell>
                      <TableCell>
                        {dba.users[0]?.name || "Unassigned"}
                      </TableCell>
                      <TableCell>
                        {new Date(
                          dba.created_at || dba.updated_at,
                        ).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          sx={{ textTransform: "capitalize" }}
                          label={
                            dba?.last_status_log?.status?.name || "undefine"
                          }
                          color={
                            dba?.last_status_log?.status?.name === "Completed"
                              ? "success"
                              : dba?.last_status_log?.status?.name ===
                                  "Underwriting"
                                ? "warning"
                                : "default"
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Can
                          permission="dba-view"
                          fallback={
                            <Box sx={{ width: 40, display: "inline-block" }} />
                          }
                        >
                          <IconButton
                            onClick={(event) => {
                              setSelectedDba(dba);
                              setAnchorEl(event.currentTarget);
                            }}
                            disabled={processingId === dba.id}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Can>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {!tableLoading && dbaData.length > 0 && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page - 1}
              onPageChange={(_, newPage) => {
                handlePageChange(newPage + 1);
              }}
              rowsPerPage={pagination.pageSize}
              onRowsPerPageChange={(e) => {
                handleRowsPerPageChange(Number(e.target.value));
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Rows per page"
            />
          )}
        </Paper>

        <DropdownMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <Can permission="dba-view">
            <MenuItem
              onClick={() => {
                handleView(selectedDba);
                setAnchorEl(null);
              }}
            >
              View
            </MenuItem>
          </Can>
          <Can permission="dba-edit">
            <MenuItem
              onClick={() => {
                handleEdit(selectedDba);
                setAnchorEl(null);
              }}
            >
              Edit
            </MenuItem>
          </Can>
          <Can permission="dba-status">
            <MenuItem
              onClick={() => {
                handleOpenStatusDialog(selectedDba);
                setAnchorEl(null);
              }}
            >
              Change Status
            </MenuItem>
          </Can>
          <Can permission="dba-reassign">
            <MenuItem
              onClick={() => {
                setOpenAssignDialog(true);
                setAnchorEl(null);
              }}
            >
              Assign DBA to User
            </MenuItem>
          </Can>
          <Can permission="dba-delete">
            <MenuItem
              onClick={() => {
                handleDelete(selectedDba);
                setAnchorEl(null);
              }}
            >
              Delete
            </MenuItem>
          </Can>
        </DropdownMenu>

        <DbaStatusChangeDialog
          open={openStatusDialog}
          onClose={handleCloseStatusDialog}
          onConfirm={handleChangeStatus}
          selectedDba={selectedDba}
          statusOptions={statusOptions}
          value={newStatus}
          onChange={setNewStatus}
          processingId={processingId}
        />

        <DbaAssignUserDialog
          open={openAssignDialog}
          onClose={() => setOpenAssignDialog(false)}
          onAssign={handleAssign}
          selectedDba={selectedDba}
          usersList={usersList}
          value={selectedUser}
          onChange={setSelectedUser}
          processingId={processingId}
        />
      </Container>
    </PageLayout>
  );
};

export default SalesDbaListing;
